import type {
  SpouseDeclarationSnapshot,
  SpouseIneligibilityReason,
  SpouseSnapshot,
  YesNo,
} from './spouseSectionTypes';

export const EMPLOYEE_INCOME_LIMIT = 9_000_000;
export const SPOUSE_INCOME_LIMIT = 950_000;

export function parseIncomeYen(raw: string): number {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return 0;
  return Number(digits);
}

export function formatIncomeYen(n: number): string {
  return n.toLocaleString('ja-JP');
}

export function evaluateSpouseEligibility(
  decl: SpouseDeclarationSnapshot,
  spouse: SpouseSnapshot
): SpouseIneligibilityReason | null {
  if (decl.sharedLivelihood === 'no') return 'no_shared_livelihood';
  if (parseIncomeYen(decl.employeeEstimatedIncome) > EMPLOYEE_INCOME_LIMIT) {
    return 'employee_income_over_limit';
  }
  if (decl.spouseFamilyWorker === 'yes') return 'spouse_family_worker';
  if (parseIncomeYen(spouse.estimatedIncome) > SPOUSE_INCOME_LIMIT) {
    return 'spouse_income_over_limit';
  }
  return null;
}

export function isSpouseEligible(
  decl: SpouseDeclarationSnapshot,
  spouse: SpouseSnapshot
): boolean {
  return evaluateSpouseEligibility(decl, spouse) === null;
}

/** Build spouse Section 2 snapshot from tax_declaration header + tax_dependent (spouse API). */
export function buildSpouseDeclarationFromApi(
  declaration: {
    employeeEstimatedIncome?: number | string | null;
    spouseAddedInSection?: boolean | null;
  } | null | undefined,
  spouseApi: {
    sharedLivelihood?: boolean | null;
    familyWorker?: boolean | null;
    withholdingFlag?: boolean | null;
    estimatedIncome?: number | string | null;
    changeDuringYear?: boolean | null;
    changeDate?: string | null;
    changeReason?: string | null;
    fullName?: string | null;
  } | null | undefined
): SpouseDeclarationSnapshot {
  const spouse = spouseApi
    ? {
        empFamilyId: null,
        fullName: spouseApi.fullName?.trim() ?? '',
        fullNameKana: '',
        myNumber: '',
        dateOfBirth: '',
        livingInSameHousehold: '',
        ifNotAddress: '',
        nonResidentFlag: '',
        nonResidentReason: '',
        disabilityCategory: '',
        estimatedIncome:
          spouseApi.estimatedIncome == null
            ? ''
            : typeof spouseApi.estimatedIncome === 'number'
              ? String(Math.trunc(spouseApi.estimatedIncome))
              : spouseApi.estimatedIncome.trim().replace(/,/g, ''),
        changeDuringYear:
          spouseApi.changeDuringYear != null
            ? spouseApi.changeDuringYear
              ? 'yes'
              : 'no'
            : spouseApi.changeDate?.trim() || spouseApi.changeReason?.trim()
              ? 'yes'
              : ('' as YesNo | ''),
        changeDate: spouseApi.changeDate?.trim() ?? '',
        changeReason: spouseApi.changeReason?.trim() ?? '',
      }
    : {
        empFamilyId: null,
        fullName: '',
        fullNameKana: '',
        myNumber: '',
        dateOfBirth: '',
        livingInSameHousehold: '',
        ifNotAddress: '',
        nonResidentFlag: '',
        nonResidentReason: '',
        disabilityCategory: '',
        estimatedIncome: '',
        changeDuringYear: '' as YesNo | '',
        changeDate: '',
        changeReason: '',
      };

  const employeeIncomeRaw = declaration?.employeeEstimatedIncome;
  const employeeEstimatedIncome =
    employeeIncomeRaw == null
      ? ''
      : typeof employeeIncomeRaw === 'number'
        ? String(Math.trunc(employeeIncomeRaw))
        : employeeIncomeRaw.trim().replace(/,/g, '');

  const decl: SpouseDeclarationSnapshot = {
    employeeEstimatedIncome,
    sharedLivelihood:
      spouseApi?.sharedLivelihood === true
        ? 'yes'
        : spouseApi?.sharedLivelihood === false
          ? 'no'
          : '',
    spouseFamilyWorker:
      spouseApi?.familyWorker === true ? 'yes' : spouseApi?.familyWorker === false ? 'no' : '',
    spouseAddedInSection: declaration?.spouseAddedInSection === true,
    withholdingEligible: spouseApi?.withholdingFlag === true,
    ineligibilityReason: '',
    sectionOutcome: '',
  };

  const reason = evaluateSpouseEligibility(decl, spouse);
  if (reason) {
    return {
      ...decl,
      ineligibilityReason: reason,
      sectionOutcome: 'ineligible',
      withholdingEligible: false,
    };
  }

  const spouseIncomeEntered = parseIncomeYen(spouse.estimatedIncome) > 0;
  const gatesComplete =
    decl.sharedLivelihood === 'yes' &&
    (decl.spouseFamilyWorker === 'yes' || decl.spouseFamilyWorker === 'no') &&
    parseIncomeYen(decl.employeeEstimatedIncome) > 0 &&
    spouseIncomeEntered;

  if (gatesComplete || spouseApi?.withholdingFlag === true) {
    return {
      ...decl,
      sectionOutcome: 'eligible',
      withholdingEligible: true,
      ineligibilityReason: '',
    };
  }

  return decl;
}

export type IneligibilityReconcileResult =
  | { action: 'unchanged' }
  | { action: 'sync_reason'; reason: SpouseIneligibilityReason }
  | { action: 'cleared'; resumeStep: 'confirmFullName' | 'spouseEstimatedIncome' };

/** Re-evaluate stored ineligible outcome against current answers (fixes stale DB flags). */
export function reconcileStoredIneligibility(
  decl: SpouseDeclarationSnapshot,
  spouse: SpouseSnapshot
): IneligibilityReconcileResult & { decl: SpouseDeclarationSnapshot } {
  if (decl.sectionOutcome !== 'ineligible') {
    return { action: 'unchanged', decl };
  }
  const currentReason = evaluateSpouseEligibility(decl, spouse);
  if (currentReason) {
    if (currentReason !== decl.ineligibilityReason) {
      return {
        action: 'sync_reason',
        reason: currentReason,
        decl: { ...decl, ineligibilityReason: currentReason },
      };
    }
    return { action: 'unchanged', decl };
  }
  const resumeStep =
    parseIncomeYen(spouse.estimatedIncome) > 0 ? 'confirmFullName' : 'spouseEstimatedIncome';
  return {
    action: 'cleared',
    resumeStep,
    decl: {
      ...decl,
      sectionOutcome: 'eligible',
      ineligibilityReason: '',
      withholdingEligible: true,
    },
  };
}

/** When section was marked ineligible, re-check after a gate-field change. */
export function mergeEligibilityIntoDeclPatch(
  decl: SpouseDeclarationSnapshot,
  spouse: SpouseSnapshot,
  patch: Partial<SpouseDeclarationSnapshot>,
  spousePatch?: Partial<SpouseSnapshot>
): Partial<SpouseDeclarationSnapshot> {
  if (decl.sectionOutcome !== 'ineligible') return patch;
  const mergedDecl = { ...decl, ...patch };
  const mergedSpouse = { ...spouse, ...spousePatch };
  const reason = evaluateSpouseEligibility(mergedDecl, mergedSpouse);
  if (reason) {
    return {
      ...patch,
      ineligibilityReason: reason,
      sectionOutcome: 'ineligible',
      withholdingEligible: false,
    };
  }
  return {
    ...patch,
    ineligibilityReason: '',
    sectionOutcome: 'eligible',
    withholdingEligible: true,
  };
}

export function clearedIneligibilityResumeStep(
  spouse: SpouseSnapshot
): 'confirmFullName' | 'spouseEstimatedIncome' {
  return parseIncomeYen(spouse.estimatedIncome) > 0 ? 'confirmFullName' : 'spouseEstimatedIncome';
}

export function ynToBool(v: YesNo | ''): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}
